import { useEffect, useState } from "react";
import { sessionFetch } from "../api/http";

type Status = {
  ok: boolean;
  version: string;
  build_version?: string;
  now: string;
  started_at: string;
  commit: string;
};

export type ServiceStatusHealth = 'checking' | 'online' | 'offline';

export interface ServiceStatusSnapshot {
  health: ServiceStatusHealth;
  version: string | null;
  buildVersion: string | null;
  commit: string | null;
}

interface ServiceStatusProps {
  onStatusChange?: (status: ServiceStatusSnapshot) => void;
  compact?: boolean;
}

export default function ServiceStatus({
  onStatusChange,
  compact = false,
}: ServiceStatusProps = {}) {
  const [s, setS] = useState<Status | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // `/ai/*` is proxied through the server behind requireUser, so the status
    // poll has to carry the staff bearer token or it always answers 401.
    (async () => {
      try {
        const r = await sessionFetch("/ai/status");
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const json = (await r.json()) as Status;
        if (active) setS(json);
      } catch {
        if (active) setErr("API status unavailable. Check the service connection.");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const health: ServiceStatusHealth = err || (s && !s.ok)
    ? 'offline'
    : s
      ? 'online'
      : 'checking';

  useEffect(() => {
    onStatusChange?.({
      health,
      version: s?.version || null,
      buildVersion: s?.build_version || null,
      commit: s?.commit || null,
    });
  }, [health, onStatusChange, s]);

  if (health === 'offline') {
    const message = err || 'AI service is offline. Check the service connection.';
    return (
      <div
        className={`service-status-bug service-status-bug--down text-xxs${compact ? ' service-status-bug--compact' : ''}`}
        data-health="down"
        title={message}
        aria-label={message}
      >
        <span className="service-status-bug__dot" aria-hidden="true" />
        <span className="service-status-bug__label">
          {compact ? 'AI' : 'AI service'}
        </span>
        {!compact ? <span className="service-status-bug__state">offline</span> : null}
      </div>
    );
  }

  if (health === 'checking' || !s) {
    return (
      <div
        className={`service-status-bug text-xxs${compact ? ' service-status-bug--compact' : ''}`}
        data-health="checking"
        aria-label="AI service status: Checking"
      >
        <span className="service-status-bug__dot service-status-bug__dot--muted" aria-hidden="true" />
        <span className="service-status-bug__label">
          {compact ? 'AI' : 'AI service'}
        </span>
        {!compact ? <span className="service-status-bug__state">checking</span> : null}
      </div>
    );
  }

  const serviceCommit = String(s.commit || '').trim();
  const shortServiceCommit = serviceCommit ? serviceCommit.slice(0, 8) : 'unknown';
  const serviceBuildVersion = String(s.build_version || `${s.version || 'unknown'}+${shortServiceCommit}`);
  const serviceLabel = `AI service online. API contract version ${s.version || 'unknown'}. Build ${serviceBuildVersion}. AI service commit ${serviceCommit || 'unknown'}.`;

  return (
    <div
      className={`service-status-bug text-xxs${compact ? ' service-status-bug--compact' : ''}`}
      data-health="ok"
      title={`${serviceLabel}\nStarted ${new Date(s.started_at).toLocaleString()}`}
      aria-label={serviceLabel}
    >
      <span className="service-status-bug__dot service-status-bug__dot--ok" aria-hidden="true" />
      <span className="service-status-bug__label">
        {compact ? 'AI' : 'AI service'}
      </span>
      {!compact ? (
        <>
          <span className="service-status-bug__state">online</span>
          <span className="service-status-bug__detail" aria-hidden="true">
            build {serviceBuildVersion}
          </span>
        </>
      ) : null}
    </div>
  );
}
